'use client';

import type { ReactNode } from 'react';
import {
  CopilotMergeProposalResolvedSchema,
  CopilotRevertPreviewSchema,
  isCopilotMergeReviseProposal,
} from '@terra/shared-contracts-applications';
import { MarkdownPreview } from '../../../../markdown-preview';
import type { HITLContentContext } from '../../tool-config-types';
import {
  AssetMergeHitlSections,
  type AssetMergeHitlSection,
} from './asset-merge-hitl-sections';

const readStringArray = (value: unknown): string[] | undefined => {
  if (!Array.isArray(value) || value.length === 0) {
    return undefined;
  }

  if (!value.every((item) => typeof item === 'string' && item.length > 0)) {
    return undefined;
  }

  return value;
};

const readAssetMergeSections = (
  args: Record<string, unknown> | undefined,
): AssetMergeHitlSection[] | undefined => {
  if (!args) {
    return undefined;
  }

  const revert = CopilotRevertPreviewSchema.safeParse(args);

  if (revert.success) {
    return [
      { title: 'Restore', parties: revert.data.restored },
      { title: 'Un-merge from', parties: revert.data.remaining },
    ];
  }

  const merge = CopilotMergeProposalResolvedSchema.safeParse(args);

  if (merge.success) {
    const isRevise = isCopilotMergeReviseProposal(merge.data);

    return [
      {
        title: isRevise ? 'Revised path' : 'Keep',
        parties: [merge.data.survivor],
      },
      {
        title: isRevise ? 'Original' : 'Absorb',
        parties: merge.data.losers,
      },
    ];
  }

  const assetIds = readStringArray(args.assetIds);

  if (assetIds) {
    return [
      {
        title: 'Restore',
        parties: assetIds.map((assetId) => ({ assetId })),
      },
    ];
  }

  return undefined;
};

const renderAssetMergeFallback = (args: unknown): ReactNode => {
  const source =
    typeof args === 'string' ? args : JSON.stringify(args, null, 2);

  return <MarkdownPreview source={source} fontSize={12} compact />;
};

export const renderAssetMergeHitlContent = (
  ctx: HITLContentContext,
): ReactNode => {
  const sections = readAssetMergeSections(ctx.args);

  if (sections) {
    return <AssetMergeHitlSections sections={sections} />;
  }

  return renderAssetMergeFallback(ctx.args);
};
